import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum DiversifiedBotanicalsPOorderDetailsKeys {
  fobPoint = 'fobPoint', // e.g., FOB Destination
  shipVia = 'shipVia', // shipping method/carrier
  orderedBy = 'orderedBy', // name of person placing order
  ordererPhone = 'ordererPhone', // orderer's phone number
}

export enum DiversifiedBotanicalsPOLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  supplierId = 'supplierId', // internal supplier id
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  promisedByDate = 'promisedByDate', // date by which the item is promised to be delivered
  uom = 'uom', // unit of measurement
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const DiversifiedBotanicalsPOaddressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type DiversifiedBotanicalsPOFileAddressData = yup.InferType<
  typeof DiversifiedBotanicalsPOaddressInfoSchema
>

export const DiversifiedBotanicalsPOIssuerAddressInfoSchema =
  DiversifiedBotanicalsPOaddressInfoSchema.shape({
    website: yup.string().url().nullable().optional(),
  })
export type DiversifiedBotanicalsPOFileIssuerAddressData = yup.InferType<
  typeof DiversifiedBotanicalsPOIssuerAddressInfoSchema
>

export const DiversifiedBotanicalsPOFileDataSchema = yup.object().shape({
  issuer: DiversifiedBotanicalsPOIssuerAddressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  vendorId: yup.string().nullable().optional(),
  destination: DiversifiedBotanicalsPOaddressInfoSchema,
  vendor: DiversifiedBotanicalsPOaddressInfoSchema,
  orderDetails: yup.object().shape({
    fobPoint: yup.string().nullable().optional(),
    shipVia: yup.string().nullable().optional(),
    orderedBy: yup.string().nullable().optional(),
    ordererPhone: yup.string().nullable().optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        supplierId: yup.string().nullable().optional(),
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        promisedByDate: yup.string().nullable().optional(),
        uom: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  decimalPlacesCount: yup.string().nullable().optional(),
  taxTotal: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
})

export type DiversifiedBotanicalsPOFileData = yup.InferType<
  typeof DiversifiedBotanicalsPOFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<DiversifiedBotanicalsPOFileData>

export const DiversifiedBotanicalsPOFileMappings: RequiredFileData = {
  issuer: {
    attn: '1a9c6b54-4f2e-4d93-bf4b-9f7b6c2a9d01',
    companyName: '7e4c1d8a-1d3a-44cb-9b8b-6e0a4a9f8d2b',
    addressLine1: '2d4f9b3c-6e44-4c71-9c7a-8e9b6d0a1f52',
    addressLine2: '9b6a7f44-7e3d-4c0b-8e91-3d2f5c4a6b18',
    city: '6c9b8d2e-4a7f-4f9b-9a5e-1c3d2b8e7f04',
    state: 'b3d2a9e6-7f1c-4e8b-b5a9-9c8e6d2f4a01',
    zipCode: '4e8b9a6d-1c2f-43a7-9b0e-7d6a5c3f8b42',
    country: '0f4b9a8c-6e7d-4a2b-9c3e-1d5f8b6a2c90',
    phoneNumber: 'c5a9e6d3-4b1f-49c8-8f2a-7d9b0e6c3a41',
    emailAddress: '8a7d2c4f-5b9e-41a6-9e3c-0d6b8f1a4c52',
    website: '3d1a9f4c-6b2e-4e7d-b5c8-9a0f2c8e6d41',
    displayMultipleLines: 'f6b4c1e9-8d5a-47c0-b2f9-9a7e6d3c1b04',
  },
  orderNumber: '4a7e9b6d-5c1f-4b8a-a3d2-1e0f9c8b7a64',
  paymentTerms: 'b8f4c1a6-2d9e-4a7b-8c5f-3e1d9a6b0c42',
  freightTerms: '9c2f6b0a-8d4e-4f5a-9b1c-7e3d6a8c1f42',
  dateIssued: '7d8b4c2f-1e6a-4b9d-a3c0-9f5e6a1b8d24',
  dateReady: '5e1b9a4c-0d6f-48b7-9a2c-8d3f6e7b1a94',
  vendorId: 'a4d9f8e1-7c6b-4e5f-9a2d-1c3b0f8e6a74',
  destination: {
    attn: '9e4b7c6a-1d5f-4b8e-a0c3-2f6d8a1e9b54',
    companyName: '2c7b1a8d-9e4f-4a5b-b6d3-0f9c8e1a4d52',
    addressLine1: '8f5d4e9a-2b1c-4e7a-9d6b-3c0f8a1e5b42',
    addressLine2: 'c9a5e2f4-6b7d-4e8a-b1d3-0f9c8a7e5d42',
    city: 'e1d9b2a6-7c4f-4b8e-9a0d-5f3c6a8b4e12',
    state: '4c9a8e6b-1d5f-4a7c-b0e2-3f6d9a8b2c41',
    zipCode: '0b2a8c7e-6d9f-4a5e-b3c1-4f8d9e6a7c42',
    country: '7b1a9e4c-8d5f-4a2e-b6c3-0f9d8a6e5b24',
    phoneNumber: 'd6f4c8e9-1a2b-4d7e-9c5f-8b3a0e6d1f42',
    emailAddress: 'a9b1c4d6-8f2e-4b7a-9e5d-3c0f6a8e1b42',
    displayMultipleLines: '1f9b4a6e-8d5c-4a2f-b7e3-9c0d8a6b5f42',
  },
  vendor: {
    attn: 'f4b6a1c9-7e5d-4a8b-b0e2-9d3c8a6f1e42',
    companyName: 'c1a8f4e9-7b5d-4e6a-b3f2-9c0d6a8e1b42',
    addressLine1: '9a2c6f4d-5b8e-4a7c-1e3f-0d8b6a9c4e42',
    addressLine2: 'b6c9f4a8-5e2d-4a7e-1f3c-8d0a9b6e4c42',
    city: 'e7d6b9a1-4f2c-4e5a-8b3d-9c0f6a1e8b42',
    state: '3a1e6b9c-4d8f-4e7a-b5c2-0d9a8f6e4b42',
    zipCode: '8b9e6c4a-1f5d-4a7e-b3c2-9d0f6a8e1b42',
    country: '2e9b8a6d-5f1c-4a7e-b3d0-9f6c4a8e1b42',
    phoneNumber: '5b6c8a1e-9d4f-4a7e-b3c2-0d9f6a8e1b42',
    emailAddress: 'a6b8e1c9-2d5f-4a7e-b3c0-9f6d8a4e1b42',
    displayMultipleLines: '4b8a6e9c-1d5f-4a7e-b3c2-9f0d6a8e1b42',
  },
  orderDetails: {
    fobPoint: '9c8f6b2e-1a4d-4b7e-a5c0-3d6f8a9e1b42',
    shipVia: 'b6d8c4a9-1f5e-4a7e-b3c0-9f6d8a2e1b42',
    orderedBy: '8e4b1c9a-6d5f-4a7e-b3c2-9f0d6a8e1b42',
    ordererPhone: '2a6c9e8b-4d5f-4a7e-b3c0-9f6d1a8e4b42',
  },
  items: [
    {
      supplierId: 'f1c9b8a6-4d5e-4a7e-b3c2-9f0d6a8e1b42',
      itemNumber: null,
      itemName: '8a1c6e9b-5d4f-4a7e-b3c0-9f6d8a2e1b42',
      name: '9e6b4a1c-8d5f-4a7e-b3c2-0f9d6a8e1b42',
      description: '4e6b8c1a-9d5f-4a7e-b3c0-9f6d8a2e1b42',
      promisedByDate: 'b8a4c6e9-1d5f-4a7e-b3c2-9f0d6a8e1b42',
      uom: '6e8b9a4c-1d5f-4a7e-b3c0-9f6d8a2e1b42',
      itemQty: '1c9b6e8a-4d5f-4a7e-b3c2-9f0d6a8e1b42',
      unitPrice: '9b8e4a1c-6d5f-4a7e-b3c0-9f6d8a2e1b42',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: 'a9e6c1b8-4d5f-4a7e-b3c2-9f0d6a8e1b42',
  decimalPlacesCount: '3b9a8e6c-1d5f-4a7e-b3c0-9f6d8a2e1b42',
  taxTotal: '8b6a1e9c-4d5f-4a7e-b3c0-9f6d8a2e1b42',
  agreement: 'c4a8e6b1-9d5f-4a7e-b3c2-0f9d6a8e1b42',
  instructions: '6b9a8e4c-1d5f-4a7e-b3c0-9f6d8a2e1b42',
  useLocaleQuantityFormatting: '1e8a9b6c-4d5f-4a7e-b3c2-9f0d6a8e1b42',
}
